import type { SponsorCampaign } from "../campaignTypes";
import { drSquatchCampaign } from "./dr-squatch";

/**
 * Anonymized The Taping case study (Dr. Squatch season).
 * Served at /partners/draft-kings with the existing passphrase; no DraftKings branding.
 */
export const draftKingsCampaign: SponsorCampaign = {
  ...drSquatchCampaign,
  campaignSlug: "draft-kings",
  sponsorName: "ComedyUO",
  sponsorLogo: "/images/Comedy_UO_logo_whiteversion_edited.png",
  sponsorWordmark: "ComedyUO",
  comedyuoLogo: "/comedyuo-logo-white.png",
  accentColor: "#72BB3B",
  eyebrow: "Case study",
  campaignName: "The taping: flagship content offering case study",
  shareTitle: "ComedyUO The taping case study",
  shareImage: "https://storage.googleapis.com/cuo-website/Head%20Image.jpg",
  headline: "Our flagship content offering: the taping.",
  headlineLine2: "A real Dr. Squatch case study.",
  campaignSummary:
    "This case study details the program we built to promote Dr. Squatch by activating their brand through our quarterly content tapings. The partnership supported their brand awareness efforts and incorporated trial touchpoints to build consideration with potential new customers.",
  heroCtaLabel: "See the results",
  heroImageAlt:
    "ComedyUO taping with brand integration on stage, used as a case study of The Taping offering",
  sampleDisclaimer:
    "Confidential. Do not distribute without expressed written consent from Comedy Underground Overground.",
  featuredMetric: {
    ...drSquatchCampaign.featuredMetric,
    value: "22,415,279",
    detail:
      "22,415,279 total views generated across Instagram, TikTok, YouTube, and Snapchat",
  },
  offeringOverview: {
    id: "the-taping",
    eyebrow: "",
    title: "Program overview",
    summary:
      "Dr. Squatch joined us as a sponsor of four content tapings over the course of a year. Each taping captured comic sets that we developed into short and long form content online. In-person audiences were met with a branded on-site experience and digital content bore the Squatch logo for a tasteful but impactful brand footprint.",
    contentEyebrow: "The content",
    contentTitle: "What the season produced",
    contentBody:
      "Specific output from the four-taping season across ComedyUO channels:",
    hubs: ["Instagram", "TikTok", "YouTube", "Snapchat", "Facebook", "YouTube Shorts"],
    outputItems: [
      "32 full-length YouTube videos (plus compilations)",
      "84 YouTube Shorts",
      "59 Instagram Reels",
      "80 TikTok posts",
    ],
    benefitsEyebrow: "",
    benefitsTitle: "How we delivered",
    benefits: [
      "Sponsored content posted about twice a week on ComedyUO channels for the full campaign window",
      "Squatch logo watermarked on clips and sets from the sponsored tapings",
      "Product trial offered to every on-site guest",
      "560 total attendees sampled across the four tapings",
      "On-stage branding and room signage at each taping",
      "Complimentary tickets to the sponsored tapings",
      "Night-of branded Stories posted to @comedyuo on Instagram",
    ],
  },
  glanceSection: {
    eyebrow: "",
    title: "Campaign at a glance",
  },
  glanceFields: [
    {
      id: "views",
      label: "Total views",
      value: "22,415,279",
    },
    {
      id: "engagements",
      label: "Engagements",
      value: "600,669",
    },
    {
      id: "shorts",
      label: "Short-form clips",
      value: "223 across IG, TikTok, and YouTube Shorts",
    },
    {
      id: "full-sets",
      label: "Full-length sets",
      value: "32",
    },
    {
      id: "duration",
      label: "Branded content run",
      value: "12 months",
    },
    {
      id: "sampling",
      label: "Total attendees sampled",
      value: "560",
    },
    {
      id: "posts",
      label: "Posts published",
      value: "255",
    },
    {
      id: "er",
      label: "Engagement rate",
      value: "7.5% on IG, TikTok, and YouTube",
    },
  ],
  activationsSection: {
    eyebrow: "",
    title: "The taping in practice",
    description:
      "Each taping was produced in a unique venue, with natural brand integrations like on-site signage, backdrop branding, and sampling setups to engage guests.",
  },
  activations: [
    {
      id: "furnish-green",
      number: 1,
      title: "Furnish Green",
      date: "August 14, 2025",
      location: "Furnish Green, NYC",
      talent:
        "Gabby Bryan, Kevin Dombrowski, Olivia Carter, Ahri Findling, Reg Thomas, Brian Scolaro, Peter Revello, Lizzie Cassidy, Ian Lara, Jason Choi, Leonard Outsz",
      description:
        "Season opener inside a vintage furniture warehouse. Soft seating, open floor, and a room that photographed like a living-room stage.",
      metrics: [],
      photos: drSquatchCampaign.activations[0].photos,
      contentLinks: [
        {
          id: "photos",
          label: "Photo gallery",
          href: "https://drive.google.com/drive/folders/1yMIBn9tOchGiYHmecfr9wVx-nEN-VI7i?usp=sharing",
        },
      ],
    },
    {
      id: "acme-smoked-fish",
      number: 2,
      title: "ACME Smoked Fish",
      date: "October 10, 2025",
      location: "ACME Smoked Fish, Brooklyn",
      talent:
        "Gabby Bryan, Eva Evans, Tommy Brennan, Peter Revello, Sherrod Small",
      description:
        "A working smokehouse in Brooklyn. Industrial room, tight audience, and a branded set that put Squatch in frame all night.",
      metrics: [],
      photos: drSquatchCampaign.activations[1].photos,
      contentLinks: [
        {
          id: "photos",
          label: "Photo gallery",
          href: "https://drive.google.com/drive/folders/1J52efnDJT7cdDyxHuSP2nG65ExxJ5ODT?usp=sharing",
        },
      ],
    },
    {
      id: "great-jones",
      number: 3,
      title: "Great Jones Distillery",
      date: "February 5, 2026",
      location: "Great Jones Distillery, NYC",
      talent:
        "Tina Friml, Stewart Fullerton, Nathan Macintosh, Maddy Smith, Kevin Dombrowski, Geoffrey Asmus, Eric Neumann, Derek Gaines, Dan Naturman, Asha Ward, Andy Hanes",
      description:
        "A downtown distillery taping. Bar-adjacent seating, warm light, and product sampling woven into the guest experience.",
      metrics: [],
      photos: drSquatchCampaign.activations[2].photos,
      contentLinks: [
        {
          id: "photos",
          label: "Photo gallery",
          href: "https://drive.google.com/drive/folders/1FeAYjsbnVpV-FnuY2skjpxWZCEnNRE3F?usp=sharing",
        },
      ],
    },
    {
      id: "paragon-sports",
      number: 4,
      title: "Paragon Sports",
      date: "April 29 - May 2, 2026",
      location: "Paragon Sports, NYC",
      talent:
        "Ray, Philipp Kostelecky, Peter Revello, Ola, Marie, Lizzie Cassidy, Lev Fer, Kevin Dombrowski, Ian Lara, Erin, Elle",
      description:
        "Season finale in the cozy hiking room of New York's oldest specialty sporting goods\u00a0store.",
      metrics: [],
      photos: drSquatchCampaign.activations[3].photos,
      contentLinks: [
        {
          id: "photos",
          label: "Photo gallery",
          href: "https://drive.google.com/drive/folders/17yjHSiUatrGLuVpdjyZ0egIYNB5e-2iz?usp=sharing",
        },
      ],
    },
  ],
  platformPerformance: {
    eyebrow: "",
    title: "The numbers",
    description:
      "These tapings perform well in both long and short form, with Snapchat and Instagram Reels garnering the bulk of views while YouTube captures attention for longer periods of time.",
    rows: [
      {
        id: "snap",
        platform: "Snapchat",
        contentNote: "Public Stories and Spotlight, campaign-level",
        impressions: "13,610,832",
        impressionsRaw: 13610832,
        sharePercent: 0,
      },
      {
        id: "ig",
        platform: "Instagram Reels",
        contentNote: "59 posts across four tapings",
        impressions: "6,307,801",
        impressionsRaw: 6307801,
        sharePercent: 0,
      },
      {
        id: "yt",
        platform: "YouTube",
        contentNote:
          "84 Shorts (1,192,815 views) and 32 full sets plus compilations (884,703 views)",
        impressions: "2,077,518",
        impressionsRaw: 2077518,
        sharePercent: 0,
      },
      {
        id: "tt",
        platform: "TikTok",
        contentNote: "80 posts",
        impressions: "419,128",
        impressionsRaw: 419128,
        sharePercent: 0,
      },
    ],
    metricColumnLabel: "Views",
    totalImpressionsLabel: "Total views",
    totalImpressionsValue: "22,415,279",
    secondaryStat: {
      label: "Engagement rate",
      value: "7.5%",
      detail:
        "600,669 engagement actions across Instagram, TikTok, and YouTube (likes, comments, shares, saves, reposts). Snapchat is excluded because engagement works differently there.",
    },
    showShareColumn: false,
    showTotalRow: false,
  },
  kpiSection: {
    eyebrow: "The numbers",
    title: "How the campaign performed",
    description: "",
    groups: [],
  },
  gallery: {
    eyebrow: "In the room",
    title: "Photos",
    description: "",
    images: [],
  },
  assets: {
    ...drSquatchCampaign.assets,
    eyebrow: "",
    title: "Download this case study as a PDF",
    description:
      "A print-ready version of this page: The Taping offering, with the Dr. Squatch case study numbers and taping breakdown.",
  },
  closing: {
    thankYouHeadline: "Why this case study",
    summary:
      "The Taping is ComedyUO's flagship content partnership format. We license comedian material, integrate brand signage and watermarking, then push that content across our channels for maximum exposure. This is a real and recent example of the impact sponsors can have through creative partnerships with our team.",
    highlightCallout:
      "Dr. Squatch case study totals: 22,415,279 views, plus 600,669 engagement actions across 255 posts at a 7.5 percent engagement rate on Instagram, TikTok, and YouTube.",
    contact: drSquatchCampaign.closing.contact,
  },
  allowedEmailDomains: ["comedyuo.com"],
  nav: [
    { id: "the-taping", label: "Program", href: "#the-taping" },
    { id: "overview", label: "At a glance", href: "#overview" },
    { id: "delivered", label: "Tapings", href: "#delivered" },
    { id: "performance", label: "Numbers", href: "#performance" },
    { id: "assets", label: "Report", href: "#assets" },
  ],
};
